import React, { useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
} from "@mui/material";
import { routes } from "../constants/routes";
import Logo from "./Logo";
import { clearAuthUser } from "../utils/authUser";

// Icons
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ForumIcon from "@mui/icons-material/Forum";
import BarChartIcon from "@mui/icons-material/BarChart";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PublicIcon from "@mui/icons-material/Public";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ScienceIcon from "@mui/icons-material/Science";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import BadgeIcon from "@mui/icons-material/Badge";
import AutorenewIcon from "@mui/icons-material/Autorenew";

export const DRAWER_WIDTH = 240;
export const TOOLBAR_HEIGHT = 72;

interface NavBarProps {
  onMobileClose: () => void;
  openMobile: boolean;
}

const NavBar: React.FC<NavBarProps> = ({ onMobileClose, openMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { href: routes.METRICS_DASHBOARD, icon: <BarChartIcon />, title: "Metrics" },
    { href: routes.CONSULTANT_LIST, icon: <PersonOutlineIcon />, title: "Consultants" },
    { href: routes.SUBSCRIPTIONS, icon: <AssignmentTurnedInIcon />, title: "Subscriptions" },
    { href: routes.AUTO_RENEWALS, icon: <AutorenewIcon />, title: "Auto Renewals" },
    { href: routes.PLAN_REQUESTS, icon: <ReceiptLongIcon />, title: "Plan Requests" },
    { href: routes.CONSULTATIONS_LIST, icon: <ForumIcon />, title: "Consultations" },
    { href: routes.PATIENT_LIST, icon: <PeopleAltIcon />, title: "Patients" },
    { href: routes.NAME_CHANGE_REQUESTS, icon: <BadgeIcon />, title: "Requests" },
    { href: routes.PLAN_LIST, icon: <DescriptionIcon />, title: "Plans" },
    { href: routes.COUNTRY_LIST, icon: <PublicIcon />, title: "Countries" },
    { href: routes.COUPON_LIST, icon: <LocalOfferIcon />, title: "Coupons" },
    { href: routes.BILLING_SIMULATION, icon: <ScienceIcon />, title: "Billing Simulation" },
    { href: routes.CONTACT_MESSAGES, icon: <MarkEmailUnreadIcon />, title: "Contact Messages" },
  ];

  useEffect(() => {
    if (openMobile) {
      onMobileClose();
    }
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    clearAuthUser();
    sessionStorage.clear();
    navigate(routes.LOGIN);
  };

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      
      <Toolbar
  sx={{
    height: TOOLBAR_HEIGHT,
    justifyContent: "center",
    alignItems: "center",
  }}
>
  <Box
    sx={{
      height: 40, // 👈 control logo height here
      display: "flex",
      alignItems: "center",
    }}
  >
    <Logo style={{ height: "100%", width: "auto" }} />
  </Box>
</Toolbar>

      <Divider />

      {/* Nav Items */}
      <List sx={{ px: 1 }}>
        {navItems.map(({ href, icon, title }) => {
          const isActive = location.pathname === href;

          return (
            <ListItemButton
              key={href}
              component={Link}
              to={href}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isActive ? "primary.light" : "transparent",
                color: isActive ? "primary.main" : "text.primary",
                "&:hover": {
                  bgcolor: "primary.light",
                },
              }}
            >
              <Box sx={{ mr: 2, display: "flex" }}>{icon}</Box>
              <ListItemText primary={title} />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      <Divider />

      {/* Logout */}
      <Box sx={{ p: 2 }}>
        <ListItemButton onClick={handleLogout}>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile */}
      <Drawer
        open={openMobile}
        onClose={onMobileClose}
        variant="temporary"
        sx={{ display: { lg: "none" } }}
        PaperProps={{ sx: { width: DRAWER_WIDTH } }}
      >
        {content}
      </Drawer>

      {/* Desktop */}
      <Drawer
        open
        variant="permanent"
        sx={{ display: { xs: "none", lg: "block" } }}
        PaperProps={{ sx: { width: DRAWER_WIDTH } }}
      >
        {content}
      </Drawer>
    </>
  );
};

export default NavBar;
